/**
 * 从物料 propsSchema（zod v4 object）生成属性面板字段描述。
 * 字段的 .meta() 必须是 PropMeta；没有 meta 的字段按类型推断 setter。
 */
import { z } from 'zod';
import type { PropMeta, SetterType } from '@h5/schema';

export interface FieldDesc {
  key: string;
  meta: PropMeta;
  /** select/radio 的选项（meta.options 优先，其次 enum 推断） */
  options?: { label: string; value: string | number | boolean }[];
  min?: number;
  max?: number;
  step?: number;
}

export interface FieldGroup {
  group: string;
  fields: FieldDesc[];
}

const DEFAULT_GROUP = '属性';

/** 剥掉 default / optional / nullable 等包装，返回 [最内层 schema, 沿途找到的第一份 meta] */
function unwrap(schema: z.ZodType): { inner: z.ZodType; meta: Record<string, unknown> | undefined } {
  let cur: z.ZodType = schema;
  let meta: Record<string, unknown> | undefined = cur.meta() as Record<string, unknown> | undefined;
  for (let i = 0; i < 10; i++) {
    const def = (cur as unknown as { def: { type: string; innerType?: z.ZodType } }).def;
    if (!def || !def.innerType) break;
    if (!['default', 'optional', 'nullable', 'nonoptional', 'prefault', 'readonly', 'catch'].includes(def.type)) break;
    cur = def.innerType;
    if (!meta) meta = cur.meta() as Record<string, unknown> | undefined;
  }
  return { inner: cur, meta };
}

function inferSetter(inner: z.ZodType): SetterType {
  const type = (inner as unknown as { def: { type: string } }).def?.type;
  switch (type) {
    case 'number': return 'number';
    case 'boolean': return 'switch';
    case 'enum': return 'select';
    case 'array': return 'formFields';
    default: return 'input';
  }
}

function enumOptions(inner: z.ZodType): FieldDesc['options'] | undefined {
  if (inner instanceof z.ZodEnum) {
    return (inner.options as (string | number)[]).map((v) => ({ label: String(v), value: v }));
  }
  return undefined;
}

export function schemaToFields(schema: z.ZodType | undefined): FieldDesc[] {
  if (!schema) return [];
  const { inner: root } = unwrap(schema);
  if (!(root instanceof z.ZodObject)) return [];
  const shape = root.shape as Record<string, z.ZodType>;
  const fields: FieldDesc[] = [];
  for (const [key, fieldSchema] of Object.entries(shape)) {
    const { inner, meta } = unwrap(fieldSchema);
    const m = (meta ?? {}) as Partial<PropMeta>;
    const setter = m.setter ?? inferSetter(inner);
    const desc: FieldDesc = {
      key,
      meta: { ...m, label: m.label ?? key, setter, group: m.group ?? DEFAULT_GROUP },
    };
    const opts = m.options ?? enumOptions(inner);
    if (opts) desc.options = opts;
    // 数值范围：meta 优先，其次 zod 的 min/max
    const num = inner as unknown as { minValue?: number | null; maxValue?: number | null };
    const min = m.min ?? (typeof num.minValue === 'number' ? num.minValue : undefined);
    const max = m.max ?? (typeof num.maxValue === 'number' ? num.maxValue : undefined);
    if (min !== undefined) desc.min = min;
    if (max !== undefined) desc.max = max;
    if (m.step !== undefined) desc.step = m.step;
    fields.push(desc);
  }
  return fields;
}

/** 按 meta.group 分组，保持首次出现顺序。 */
export function groupFields(fields: FieldDesc[]): FieldGroup[] {
  const map = new Map<string, FieldDesc[]>();
  for (const f of fields) {
    const g = f.meta.group ?? DEFAULT_GROUP;
    if (!map.has(g)) map.set(g, []);
    map.get(g)!.push(f);
  }
  return [...map.entries()].map(([group, fields]) => ({ group, fields }));
}
